import { IContact, ISegmentRule } from '../../../types/contactModule';

/*
 * Segment rules: what the builder offers, how a rule is saved, how a saved
 * rule reads back, and how it is described to people.
 *
 * Saved keys follow what the contacts service evaluates
 * (PersonEntityCriteria.setFiltersFromMap): "<field>.equals",
 * "<field>.is not", "<field>.contains", "<field>.not contain",
 * "birthDate.will be in" (one date), "Age.greaterThan" and "Age.lessThan".
 * Which keys the service applies depends on the field (see APPLIED_SUFFIXES).
 * Earlier versions of this file saved "notEquals", "doesNotContain" and
 * "birthDate.equals", which the service skips; those read back as legacy
 * rules so the console can flag them.
 */

export type TSegmentDataType = 'TEXT' | 'DATE' | 'NUMBER';

export type ISegmentAttribute =
  | 'First Name'
  | 'Last Name'
  | 'Address'
  | 'City'
  | 'Country'
  | 'Gender'
  | 'Birth Date'
  | 'Email'
  | 'Created At'
  | 'Age';

/** Operators as the builder shows them. */
export type TSegmentOperator = 'is' | 'is not' | 'contains' | 'not contain' | 'more than' | 'less than';

export interface IEntitySegmentQueryFilter {
  id?: number;
  attributeName: string;
  propertyFilterParameter: string;
  filterValue: string;
  dataType: TSegmentDataType;
  objectType?: string;
  order: number;
  uuid?: string;
  organisationId?: string;
  recordStatus?: string;
  filterSpecification: 'AND_OPERATOR';
  entitySegments?: string[];
}

interface User {
  id: string;
  login: string;
}

export interface IEntitySegmentQuery {
  //this is the return type of a segment after fetching a segment
  id?: number | string;
  name: string;
  description: string;
  createdAt?: string;
  modifiedAt?: string;
  uuid?: string;
  recordStatus?: string;
  organisationId?: string;
  createdBy?: User;
  modifiedBy?: User;
  entitySegmentQueryFilters: IEntitySegmentQueryFilter[];
  personEntities?: IContact[];
}

// ── What the builder offers ──

export type TSegmentInput = 'text' | 'email' | 'date' | 'number' | 'gender' | 'country';

export interface SegmentAttributeSpec {
  label: ISegmentAttribute;
  /** The contact field the service filters on. */
  field: string;
  dataType: TSegmentDataType;
  input: TSegmentInput;
  operators: TSegmentOperator[];
  /** Quote the value when describing the rule ("First name is “Ann”"). */
  quoted: boolean;
}

const TEXT_OPERATORS: TSegmentOperator[] = ['is', 'is not', 'contains', 'not contain'];

export const SEGMENT_ATTRIBUTES: SegmentAttributeSpec[] = [
  { label: 'First Name', field: 'firstName', dataType: 'TEXT', input: 'text', operators: TEXT_OPERATORS, quoted: true },
  { label: 'Last Name', field: 'lastName', dataType: 'TEXT', input: 'text', operators: TEXT_OPERATORS, quoted: true },
  { label: 'City', field: 'city', dataType: 'TEXT', input: 'text', operators: TEXT_OPERATORS, quoted: true },
  { label: 'Country', field: 'country', dataType: 'TEXT', input: 'country', operators: TEXT_OPERATORS, quoted: false },
  { label: 'Gender', field: 'gender', dataType: 'TEXT', input: 'gender', operators: ['is', 'is not'], quoted: false },
  { label: 'Birth Date', field: 'birthDate', dataType: 'DATE', input: 'date', operators: ['is', 'is not'], quoted: false },
  { label: 'Email', field: 'emailAddress', dataType: 'TEXT', input: 'email', operators: TEXT_OPERATORS, quoted: true },
  { label: 'Age', field: 'Age', dataType: 'NUMBER', input: 'number', operators: ['is', 'is not', 'more than', 'less than'], quoted: false },
];

/**
 * Fields no longer offered for new conditions; older segments may still
 * carry them, so they still read back, describe and edit.
 *  - Address: dropped from the builder earlier.
 *  - Date added: the service compares the exact instant (midnight UTC on that
 *    day), so "Date added is" matches almost nobody and "is not" almost
 *    everybody. Offer it again once the service compares whole days.
 */
const HIDDEN_ATTRIBUTES: SegmentAttributeSpec[] = [
  { label: 'Address', field: 'address', dataType: 'TEXT', input: 'text', operators: TEXT_OPERATORS, quoted: true },
  { label: 'Created At', field: 'createdAt', dataType: 'DATE', input: 'date', operators: ['is', 'is not'], quoted: false },
];

const ALL_ATTRIBUTES: SegmentAttributeSpec[] = [...SEGMENT_ATTRIBUTES, ...HIDDEN_ATTRIBUTES];

export const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
];

/** Sentence-case attribute names: "Birth date", "Created at". */
export const ATTRIBUTE_TEXT: Record<ISegmentAttribute, string> = {
  'First Name': 'First name',
  'Last Name': 'Last name',
  Address: 'Address',
  City: 'City',
  Country: 'Country',
  Gender: 'Gender',
  'Birth Date': 'Birth date',
  Email: 'Email',
  'Created At': 'Date added',
  Age: 'Age',
};

/** How each operator reads in a sentence and in the builder. */
export const OPERATOR_TEXT: Record<TSegmentOperator, string> = {
  is: 'is',
  'is not': 'is not',
  contains: 'contains',
  'not contain': 'does not contain',
  'more than': 'is more than',
  'less than': 'is less than',
};

export function attributeSpec(label: string | null | undefined): SegmentAttributeSpec | undefined {
  if (!label) return undefined;
  return ALL_ATTRIBUTES.find((spec) => spec.label === label);
}

function specForField(field: string | null | undefined): SegmentAttributeSpec | undefined {
  if (!field) return undefined;
  return ALL_ATTRIBUTES.find((spec) => spec.field === field || spec.field.toLowerCase() === field.toLowerCase());
}

/** The saved data type for an attribute ("Birth Date" → DATE). */
export function dataTypeFor(label: string | null | undefined): TSegmentDataType {
  return attributeSpec(label)?.dataType ?? 'TEXT';
}

// ── Saving ──

/** Operator → the key suffix the contacts service reads. */
const SAVE_KEY: Record<TSegmentOperator, string> = {
  is: 'equals',
  'is not': 'is not',
  contains: 'contains',
  'not contain': 'not contain',
  'more than': 'greaterThan',
  'less than': 'lessThan',
};

/**
 * Where a field needs another key: the service treats "birthDate.equals" as
 * a no-op, but applies "birthDate.will be in" as a list of one date.
 */
const FIELD_SAVE_KEY: Record<string, Partial<Record<TSegmentOperator, string>>> = {
  birthDate: { is: 'will be in' },
};

/**
 * Internal operator for the second gender filter. Gender equality is saved
 * as both "gender.equals" and "gender.is": the service reads one of them and
 * skips the other, so either way the rule applies.
 */
export const GENDER_ALIAS_OPERATOR = 'is (gender alias)';

function normalizeValue(spec: SegmentAttributeSpec | undefined, value: unknown): string {
  const text = (value ?? '').toString().trim();
  if (spec?.input === 'gender') return text.toUpperCase();
  return text;
}

export const segmentRuleFormatter = (segmentRule: ISegmentRule): IEntitySegmentQueryFilter => {
  const spec = attributeSpec(segmentRule.attributeName);
  const field = spec?.field ?? 'firstName';
  const operator = (segmentRule.rule ?? '') as TSegmentOperator | typeof GENDER_ALIAS_OPERATOR;
  const suffix =
    operator === GENDER_ALIAS_OPERATOR
      ? 'is'
      : FIELD_SAVE_KEY[field]?.[operator as TSegmentOperator] ?? SAVE_KEY[operator as TSegmentOperator] ?? 'equals';

  return {
    attributeName: field,
    dataType: spec?.dataType ?? 'TEXT',
    filterSpecification: 'AND_OPERATOR',
    filterValue: normalizeValue(spec, segmentRule.values?.[0]),
    order: Number.isFinite(segmentRule.index as number) ? (segmentRule.index as number) : 0,
    propertyFilterParameter: `${field}.${suffix}`,
  };
};

/** A rule with an attribute, an operator and a value. */
export function isCompleteRule(rule: ISegmentRule | null | undefined): boolean {
  if (!rule) return false;
  const spec = attributeSpec(rule.attributeName);
  if (!spec || !rule.rule || !spec.operators.includes(rule.rule as TSegmentOperator)) return false;
  return !!(rule.values?.[0] ?? '').toString().trim();
}

/** A row nobody has started filling in. */
export function isBlankRule(rule: ISegmentRule | null | undefined): boolean {
  if (!rule) return true;
  return !rule.attributeName && !rule.rule && !(rule.values?.[0] ?? '').toString().trim();
}

/**
 * Rules ready to save: complete rows only, numbered in order, typed from the
 * attribute. Gender equality also gets its alias filter (see above).
 */
export function payloadRules(rules: ISegmentRule[]): ISegmentRule[] {
  const kept = rules.filter(isCompleteRule).map((rule, index) => ({
    ...rule,
    values: [normalizeValue(attributeSpec(rule.attributeName), rule.values?.[0])],
    index,
    dataType: dataTypeFor(rule.attributeName),
    connector: 'AND',
  }));
  const withAliases: ISegmentRule[] = [];
  for (const rule of kept) {
    withAliases.push(rule);
    if (attributeSpec(rule.attributeName)?.input === 'gender' && rule.rule === 'is') {
      withAliases.push({ ...rule, rule: GENDER_ALIAS_OPERATOR, id: `${rule.id ?? rule.index}-alias` });
    }
  }
  return withAliases;
}

// ── Reading back ──

/** Every key suffix any version of the console saved, as the operator it stands for. */
const DISPLAY_SUFFIX: Record<string, TSegmentOperator> = {
  equals: 'is',
  is: 'is',
  EQUALS: 'is',
  in: 'is',
  'will be in': 'is',
  'is not': 'is not',
  notEquals: 'is not',
  NOT_EQUALS: 'is not',
  not_equals: 'is not',
  contains: 'contains',
  'not contain': 'not contain',
  doesNotContain: 'not contain',
  greaterThan: 'more than',
  GREATER_THAN: 'more than',
  greater_than: 'more than',
  'will be more than': 'more than',
  lessThan: 'less than',
  LESS_THAN: 'less than',
  less_than: 'less than',
  'will be less than': 'less than',
};

/**
 * The suffixes the contacts service applies, per field
 * (PersonEntityCriteria.setFiltersFromMap). Anything else is skipped, so the
 * condition does not filter.
 */
const TEXT_SUFFIXES = ['equals', 'is', 'is not', 'contains', 'not contain'];
const APPLIED_SUFFIXES: Record<string, string[]> = {
  gender: ['equals', 'is', 'is not'],
  // "birthDate.equals" and "birthDate.is" are no-ops in the service.
  birthDate: ['will be in', 'is not'],
  createdAt: ['equals', 'is', 'is not', 'will be in'],
  Age: [
    'equals', 'is', 'EQUALS',
    'is not', 'NOT_EQUALS', 'not_equals',
    'greaterThan', 'GREATER_THAN', 'greater_than', 'will be more than',
    'lessThan', 'LESS_THAN', 'less_than', 'will be less than',
  ],
};

function serviceApplies(spec: SegmentAttributeSpec, suffix: string): boolean {
  return (APPLIED_SUFFIXES[spec.field] ?? TEXT_SUFFIXES).includes(suffix);
}

export interface ReadFilter {
  attribute: ISegmentAttribute;
  operator: TSegmentOperator;
  value: string;
  /** Saved with a key the contacts service skips, so it does not filter today. */
  legacy: boolean;
  /** The gender alias half of a pair; never shown on its own. */
  alias: boolean;
  source: IEntitySegmentQueryFilter;
}

function splitKey(filter: IEntitySegmentQueryFilter): { field: string; suffix: string } {
  const key = (filter?.propertyFilterParameter ?? '').toString();
  const dot = key.indexOf('.');
  if (dot < 0) return { field: filter?.attributeName ?? '', suffix: key };
  return { field: key.slice(0, dot) || filter?.attributeName || '', suffix: key.slice(dot + 1) };
}

export function readFilter(filter: IEntitySegmentQueryFilter): ReadFilter {
  const { field, suffix } = splitKey(filter);
  const spec = specForField(field) ?? specForField(filter?.attributeName) ?? SEGMENT_ATTRIBUTES[0];
  const operator = DISPLAY_SUFFIX[suffix] ?? 'is';
  return {
    attribute: spec.label,
    operator: spec.operators.includes(operator) ? operator : spec.operators[0],
    value: (filter?.filterValue ?? '').toString(),
    // Anything the service does not apply to this field (old keys, unknown keys) is legacy.
    legacy: !serviceApplies(spec, suffix),
    alias: spec.input === 'gender' && suffix === 'is',
    source: filter,
  };
}

/**
 * Saved filters as the rules they stand for: in saved order, with each
 * gender alias folded into its partner.
 */
export function readFilters(filters: IEntitySegmentQueryFilter[] | null | undefined): ReadFilter[] {
  const list = Array.isArray(filters) ? [...filters] : [];
  list.sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));
  const read = list.filter(Boolean).map(readFilter);
  const kept: ReadFilter[] = [];
  for (const item of read) {
    const twin = kept.find(
      (other) =>
        other.attribute === item.attribute &&
        other.operator === item.operator &&
        other.value.toUpperCase() === item.value.toUpperCase()
    );
    if (twin) {
      // Prefer the non-legacy reading of a pair.
      if (twin.legacy && !item.legacy) kept.splice(kept.indexOf(twin), 1, { ...item, alias: false });
      continue;
    }
    kept.push({ ...item, alias: false });
  }
  return kept;
}

export const convertSegmentDataForComponent = (segmentQueryFilter: IEntitySegmentQueryFilter): ISegmentRule => {
  const read = readFilter(segmentQueryFilter);
  return {
    attributeName: read.attribute,
    rule: read.operator,
    values: [read.attribute === 'Gender' ? read.value.toUpperCase() : read.value],
    connector: 'AND',
    id: segmentQueryFilter.uuid,
    dataType: segmentQueryFilter.dataType,
    index: segmentQueryFilter.order,
  };
};

/** Saved filters → builder rows, one per rule (gender aliases folded). */
export function filtersToRules(filters: IEntitySegmentQueryFilter[] | null | undefined): ISegmentRule[] {
  return readFilters(filters).map((read, index) => ({
    attributeName: read.attribute,
    rule: read.operator,
    values: [read.attribute === 'Gender' ? read.value.toUpperCase() : read.value],
    connector: 'AND',
    id: read.source.uuid ?? `rule-${index}`,
    dataType: read.source.dataType,
    index,
  }));
}

// ── Describing ──

const dateText = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

function valueText(attribute: ISegmentAttribute, value: string): string {
  const spec = attributeSpec(attribute);
  const raw = (value ?? '').toString().trim();
  if (!raw) return '(no value)';
  if (spec?.input === 'gender') {
    return GENDER_OPTIONS.find((option) => option.value === raw.toUpperCase())?.label ?? raw;
  }
  if (spec?.input === 'date') {
    const date = new Date(`${raw.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? raw : dateText.format(date);
  }
  if (spec?.input === 'number') {
    const years = Number(raw);
    return Number.isFinite(years) ? `${raw} ${years === 1 ? 'year' : 'years'}` : raw;
  }
  return spec?.quoted ? `“${raw}”` : raw;
}

/** "City contains “Gab”", "Age is more than 30 years". */
export function describeRule(attribute: string, operator: string, value: string): string {
  const spec = attributeSpec(attribute);
  if (!spec) return '';
  const op = OPERATOR_TEXT[operator as TSegmentOperator] ?? operator;
  return `${ATTRIBUTE_TEXT[spec.label]} ${op} ${valueText(spec.label, value)}`;
}

export function describeFilter(read: ReadFilter): string {
  return describeRule(read.attribute, read.operator, read.value);
}

/** One sentence for a segment's saved rules, or '' when there are none: "City contains “Gab” and age is more than 30 years". */
export function describeFilters(filters: IEntitySegmentQueryFilter[] | null | undefined): string {
  return readFilters(filters)
    .map(describeFilter)
    .filter(Boolean)
    .map((text, index) => (index ? text.charAt(0).toLowerCase() + text.slice(1) : text))
    .join(' and ');
}

export function hasLegacyFilters(filters: IEntitySegmentQueryFilter[] | null | undefined): boolean {
  return readFilters(filters).some((read) => read.legacy);
}

// ── Counting: rules → contact list query ──

export interface SegmentCriteria {
  params: Record<string, string>;
  /** Rules the contact list cannot express (left out of the estimate). */
  skipped: number;
}

function isoDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function yearsAgo(years: number, today = new Date()): Date {
  const date = new Date(today.getFullYear() - years, today.getMonth(), today.getDate());
  return date;
}

/**
 * The contact list query (PersonEntityCriteria) that picks roughly the same
 * contacts as these rules, for "matches about N contacts". Age becomes a
 * birth date range. `asSaved` skips legacy rules, the way the contacts
 * service does when it evaluates the saved segment.
 */
export function rulesToCriteria(
  rules: { attribute: string; operator: string; value: string; legacy?: boolean }[],
  options: { asSaved?: boolean } = {}
): SegmentCriteria {
  const params: Record<string, string> = {};
  let skipped = 0;
  const lower = (key: string, value: string) => {
    params[key] = params[key] && params[key] > value ? params[key] : value;
  };
  const upper = (key: string, value: string) => {
    params[key] = params[key] && params[key] < value ? params[key] : value;
  };

  for (const rule of rules) {
    if (options.asSaved && rule.legacy) continue;
    const spec = attributeSpec(rule.attribute);
    const value = (rule.value ?? '').toString().trim();
    if (!spec || !value) continue;

    if (spec.input === 'number') {
      const years = Math.floor(Number(value));
      if (!Number.isFinite(years) || years < 0) {
        skipped++;
        continue;
      }
      if (rule.operator === 'more than') {
        upper('birthDate.lessThanOrEqual', isoDay(yearsAgo(years + 1)));
      } else if (rule.operator === 'less than') {
        lower('birthDate.greaterThan', isoDay(yearsAgo(years)));
      } else if (rule.operator === 'is') {
        lower('birthDate.greaterThan', isoDay(yearsAgo(years + 1)));
        upper('birthDate.lessThanOrEqual', isoDay(yearsAgo(years)));
      } else {
        skipped++;
      }
      continue;
    }

    if (spec.field === 'createdAt') {
      // The service compares the exact instant, midnight UTC on that day,
      // not the whole day; count it the same way.
      const day = value.slice(0, 10);
      const key = rule.operator === 'is' ? 'createdAt.equals' : rule.operator === 'is not' ? 'createdAt.notEquals' : '';
      if (!key || !/^\d{4}-\d{2}-\d{2}$/.test(day)) {
        skipped++;
        continue;
      }
      params[key] = `${day}T00:00:00Z`;
      continue;
    }

    const field = spec.field;
    const normalized = spec.input === 'gender' ? value.toUpperCase() : value;
    switch (rule.operator) {
      case 'is':
        params[`${field}.equals`] = normalized;
        break;
      case 'is not':
        params[`${field}.notEquals`] = normalized;
        break;
      case 'contains':
        params[`${field}.contains`] = normalized;
        break;
      case 'not contain':
        params[`${field}.doesNotContain`] = normalized;
        break;
      default:
        skipped++;
    }
  }
  return { params, skipped };
}

export function filtersToCriteria(
  filters: IEntitySegmentQueryFilter[] | null | undefined,
  options: { asSaved?: boolean } = {}
): SegmentCriteria {
  return rulesToCriteria(readFilters(filters), options);
}

export function draftRulesToCriteria(rules: ISegmentRule[]): SegmentCriteria {
  return rulesToCriteria(
    rules.filter(isCompleteRule).map((rule) => ({
      attribute: rule.attributeName,
      operator: rule.rule ?? '',
      value: (rule.values?.[0] ?? '').toString(),
    }))
  );
}

// ── Builder navigation ──

/** Route commands for the builder page in use: New segment, or Edit segment for one uuid. */
export function builderCommands(url: string, id: string | null | undefined): string[] {
  return url.includes('/segmented-list/edit') && id
    ? ['organization', 'contacts', 'segmented-list', 'edit', id]
    : ['organization', 'contacts', 'segmented-list', 'create'];
}
